import React, {useCallback, memo} from "react";
import {AnchorArrow, AnchorStyle, RippleItem} from "./Styled";
import {clickHandle, usePreFetch, useRippleEffect} from "../utils";
import {validateField} from "../../../../utils/validation";
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import {checkUrlIsExternal} from "../../../../utils/url";
import {useHistory} from "react-router";
import {anchorLabelForExternalURL} from "../../../../utils/string";

const AnchorNonPWA = (props) => {
    const {
        disabledExternalValidation,
        linkHref,
        title,
        target,
        linkLabel,
        showLinkIcon,
        displayStyle,
        active,
        debug,
        after,
        preventDefault,
        children,
        onClickHandle,
        eventCategory,
        eventAction,
        enableRipple,
        isPWALink,
        forwardRef,
        rippleDuration,
        isUserLoggedIn,
        rel,
    } = props;
    const {ref, rippleState, createRipple} = useRippleEffect(enableRipple, rippleDuration);
    const {push} = useHistory();

    const isMobile = useSelector((state) => state.layout.isMobile);
    const attachRef = useCallback(
        (node) => {
            if (validateField(forwardRef, "object")) {
                forwardRef.current = node;
            } else if (typeof forwardRef === "function") {
                forwardRef(node);
            }
            ref.current = node;
        },
        [forwardRef, ref],
    );
    let preventDefaultVal = preventDefault;
    if (linkHref === "#") {
        preventDefaultVal = true;
    }

    const dispatch = useDispatch();
    let isSecureUrlBehindLogin = false,
        externalTrackingId = null;

    const externalTrackingIdObj = useSelector(
        (state) => state.layout.currentExternalURLSignUpTrackingKey,
    );
    if (!isUserLoggedIn && !disabledExternalValidation) {
        isSecureUrlBehindLogin = checkUrlIsExternal(linkHref);
        if (isSecureUrlBehindLogin) {
            if (isMobile) {
                externalTrackingId = externalTrackingIdObj?.mobile ?? 0;
            } else {
                externalTrackingId = externalTrackingIdObj?.laptop ?? 0;
            }
        }
    }
    const paramsObj = {
        preventDefaultVal,
        linkHref,
        onClickHandle,
        eventCategory,
        eventAction,
        dispatch,
        enableRipple,
        isPWALink,
        createRipple,
        isSecureUrlBehindLogin,
        push,
        externalTrackingId,
    };

    const {preloadHandle, mouseHoverOut} = usePreFetch();

    return (
        <React.Fragment>
            <AnchorStyle
                href={linkHref}
                {...(isSecureUrlBehindLogin
                    ? {
                          onContextMenu: (event) => {
                              event.preventDefault();
                              return false;
                          },
                      }
                    : {href: linkHref})}
                target={target}
                {...(isSecureUrlBehindLogin ? {} : {title: title})}
                displaystyle={displayStyle}
                active={active}
                ref={attachRef}
                onClick={(event) => {
                    clickHandle(event, paramsObj);
                }}
                debug={debug}
                after={after}
                enableRipple={enableRipple}
                {...(isMobile
                    ? {
                          onTouchMove: () => {
                              preloadHandle(preventDefaultVal, linkHref);
                          },
                      }
                    : {
                          onMouseEnter: () => {
                              preloadHandle(preventDefaultVal, linkHref);
                          },
                      })}
                {...(isMobile
                    ? {}
                    : {
                          onMouseLeave: () => {
                              mouseHoverOut();
                          },
                      })}
                {...(validateField(props.itemProp) ? {itemProp: props.itemProp} : "")}
                {...(validateField(props.itemType) ? {itemType: props.itemType} : "")}
                {...(validateField(rel) ? {rel: rel} : "")}
            >
                {validateField(linkLabel)
                    ? isSecureUrlBehindLogin && linkHref
                        ? anchorLabelForExternalURL(linkHref)
                        : linkLabel
                    : children}
                {validateField(rippleState, "object") && (
                    <RippleItem {...rippleState} rippleDuration={rippleDuration} />
                )}
            </AnchorStyle>
            {showLinkIcon && (
                <AnchorArrow
                    onClick={(event) =>
                        isSecureUrlBehindLogin
                            ? clickHandle(event, paramsObj)
                            : window.open(linkHref, target).focus()
                    }
                />
            )}
        </React.Fragment>
    );
};

AnchorNonPWA.propTypes = {
    linkHref: PropTypes.string,
    target: PropTypes.oneOf(["_self", "_blank", "_parent", "_top"]),
    title: PropTypes.string,
    linkLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    showLinkIcon: PropTypes.bool,
    displayStyle: PropTypes.object,
    preventDefault: PropTypes.bool,
    children: PropTypes.node,
    active: PropTypes.bool,
    onClickHandle: PropTypes.func,
    eventCategory: PropTypes.string,
    eventAction: PropTypes.string,
    linkType: PropTypes.bool,
    itemProp: PropTypes.string,
    itemType: PropTypes.string,
    after: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    enableRipple: PropTypes.bool,
    isPWALink: PropTypes.bool,
    forwardRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
            current: PropTypes.instanceOf(
                typeof Element === "undefined" ? function () {} : Element,
            ),
        }),
    ]),
    rippleDuration: PropTypes.number,
    debug: PropTypes.bool,
    isUserLoggedIn: PropTypes.bool,
    disabledExternalValidation: PropTypes.bool,
    rel: PropTypes.string,
};
AnchorNonPWA.defaultProps = {
    enableRipple: true,
};

export default memo(AnchorNonPWA);
